import { useQuery } from "@tanstack/react-query"
import api from "api"
import { AttemptStatus } from "api/resources/assessments/types"
import queries from "api/resources/learningItems/queries"
import {
  LearningItemType,
  ScheduleType,
  UpcomingPlannerItem,
} from "api/resources/learningItems/types"

export const useUpcomingPlannerItems = ({
  learningItemType,
  learningItemId,
}: {
  learningItemType: LearningItemType
  learningItemId: number
}): { isSuccess: boolean; items: UpcomingPlannerItem[] } => {
  const { data, isSuccess } = useQuery({
    queryFn: () =>
      api.learningItems.getUpcomingPlannerItems({
        urlParams: { learningItemType, learningItemId },
      }),
    queryKey: queries.upcomingPlannerItem(learningItemType, learningItemId)
      .queryKey,
  })

  if (!isSuccess) return { isSuccess: false, items: [] }

  return { isSuccess: true, items: data }
}

export type AssessmentSchedule = Extract<
  UpcomingPlannerItem,
  { scheduleType: ScheduleType.ASSESSMENT }
>

type ClassSession = Extract<
  UpcomingPlannerItem,
  { scheduleType: ScheduleType.CLASS }
>

export const findAssessmentSchedule = (
  items: UpcomingPlannerItem[]
): AssessmentSchedule | undefined =>
  items.find(
    (item): item is AssessmentSchedule =>
      item.scheduleType === ScheduleType.ASSESSMENT
  )

export const findClassSession = (
  items: UpcomingPlannerItem[]
): ClassSession | undefined =>
  items.find(
    (item): item is ClassSession => item.scheduleType === ScheduleType.CLASS
  )

/** True while the assessment hasn't happened yet (or is attemptable right
 * now) — false once it's been completed or missed, since those are past
 * states better reviewed from the curriculum than surfaced as "Upcoming". */
export const isAssessmentUpcoming = (schedule: AssessmentSchedule): boolean => {
  const status = schedule.attempt?.status
  return status !== AttemptStatus.COMPLETED && status !== AttemptStatus.MISSED
}
